/**
 * My recordings - every table recording the player has made, with download
 * links and the per-recording price (partners record free).
 * RecordingsPanel is also embedded in the partner (agent) dashboard.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, Container, Heading, HStack, Text, VStack, Badge, Spinner, useToast,
} from '@chakra-ui/react';
import { ArrowLeft, Download, Trash2, RefreshCw, Video } from 'lucide-react';
import gameApiService from '../services/gameApiService';
import { POKER_COLORS } from '../theme/designSystem';

interface RecordingFile { path: string; url: string; sizeBytes: number }
interface RecordingRow {
  id: string; tableId: string; tableName: string | null; status: string;
  startedAt: number | null; stoppedAt: number | null; durationSec: number; maxSessionSec: number;
  files: RecordingFile[]; sizeBytes: number; expiresAt: number | null; failReason: string | null; stopReason: string | null;
}

const STATUS: Record<string, { label: string; color: string }> = {
  starting: { label: 'Starting', color: 'orange' },
  recording: { label: 'Recording', color: 'red' },
  stopping: { label: 'Stopping', color: 'orange' },
  processing: { label: 'Processing', color: 'yellow' },
  ready: { label: 'Ready', color: 'green' },
  failed: { label: 'Failed', color: 'gray' },
  expired: { label: 'Expired', color: 'gray' },
};

const fmtDur = (s: number) => `${Math.floor(s / 60)}m ${String(Math.floor(s % 60)).padStart(2, '0')}s`;
const fmtSize = (b: number) => (b > 1e9 ? `${(b / 1e9).toFixed(2)} GB` : `${Math.max(1, Math.round(b / 1e6))} MB`);
const fmtDate = (t: number | null) => (t ? new Date(t).toLocaleString() : '');
const daysLeft = (t: number | null) => (t ? Math.max(0, Math.ceil((t - Date.now()) / 86400000)) : null);

export const RecordingsPanel: React.FC<{ compact?: boolean }> = ({ compact }) => {
  const toast = useToast();
  const [rows, setRows] = useState<RecordingRow[]>([]);
  const [meta, setMeta] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await gameApiService.recordingsMine();
      setRows(r.rows || []);
      setMeta(r);
    } catch (e: any) {
      toast({ title: 'Could not load recordings', description: e.message, status: 'error', duration: 4000 });
    } finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const live = rows.some((r) => ['starting', 'recording', 'stopping', 'processing'].includes(r.status));
    if (!live) return;
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [rows, load]);

  const stop = async (id: string) => {
    setBusy(id);
    try { await gameApiService.recordingsStop(id); await load(); }
    catch (e: any) { toast({ title: e.message, status: 'error', duration: 4000 }); }
    finally { setBusy(null); }
  };
  const remove = async (id: string) => {
    if (!window.confirm('Delete this recording? This cannot be undone.')) return;
    setBusy(id);
    try { await gameApiService.recordingsDelete(id); await load(); }
    catch (e: any) { toast({ title: e.message, status: 'error', duration: 4000 }); }
    finally { setBusy(null); }
  };
  return (
    <VStack align="stretch" spacing={4}>
      {meta && (
        <Box bg="whiteAlpha.100" borderRadius="lg" p={4}>
          <HStack justify="space-between" mb={1} flexWrap="wrap" gap={2}>
            <Text color="white" fontWeight="bold">
              {meta.free ? 'Recording is free for you' : `${meta.pricePssCoins} PSS Coins per recording`}
              {meta.partner && <Badge ml={2} colorScheme="green">Partner</Badge>}
            </Text>
            {!meta.free && <Text color="gray.300" fontSize="sm">Balance: {Number(meta.balance || 0).toFixed(2)} PSS Coins</Text>}
          </HStack>
          <Text color="gray.500" fontSize="xs">
            Up to {meta.maxSessionMinutes} min per recording, then it stops itself. Files kept {meta.retentionDays} days.
            {!meta.free && ' Tables opened with a partner code record free.'} Voice and video without recording are always free.
          </Text>
          {meta.enabled === false && <Text color="yellow.300" fontSize="sm" mt={2}>Recording isn't switched on yet.</Text>}
        </Box>
      )}

      <HStack justify="space-between">
        <Text color="gray.400" fontSize="sm">{rows.length} recording{rows.length === 1 ? '' : 's'}</Text>
        <Button size="xs" variant="ghost" color="gray.400" leftIcon={<RefreshCw size={12} />} onClick={load}>Refresh</Button>
      </HStack>

      {loading ? (
        <HStack justify="center" py={8}><Spinner color="cyan.300" /></HStack>
      ) : rows.length === 0 ? (
        <Box bg="whiteAlpha.50" borderRadius="lg" p={6} textAlign="center">
          <Video size={28} color="#64748b" style={{ margin: '0 auto 8px' }} />
          <Text color="gray.400" fontSize="sm">No recordings yet. Open any table and tap Record.</Text>
        </Box>
      ) : rows.map((r) => {
        const st = STATUS[r.status] || { label: r.status, color: 'gray' };
        const dl = daysLeft(r.expiresAt);
        return (
          <Box key={r.id} bg="whiteAlpha.100" borderRadius="lg" p={compact ? 3 : 4} borderLeft="3px solid" borderColor={r.status === 'ready' ? 'green.400' : r.status === 'recording' ? 'red.400' : 'gray.600'}>
            <HStack justify="space-between" align="start" flexWrap="wrap" gap={2}>
              <Box>
                <Text color="white" fontWeight="semibold">{r.tableName || 'Table'}</Text>
                <Text color="gray.400" fontSize="xs">{fmtDate(r.startedAt)} · {fmtDur(r.durationSec || 0)}{r.sizeBytes ? ` · ${fmtSize(r.sizeBytes)}` : ''}</Text>
                {r.status === 'ready' && dl !== null && <Text color={dl <= 3 ? 'orange.300' : 'gray.500'} fontSize="xs">Expires in {dl} day{dl === 1 ? '' : 's'}</Text>}
                {r.status === 'processing' && <Text color="gray.500" fontSize="xs">Your file is being prepared. This usually takes a few minutes.</Text>}
                {r.status === 'failed' && <Text color="gray.500" fontSize="xs">{r.failReason || 'The recorder could not produce a file.'}</Text>}
              </Box>
              <Badge colorScheme={st.color}>{st.label}</Badge>
            </HStack>
            <HStack mt={3} spacing={2} flexWrap="wrap">
              {r.files.map((f, i) => (
                <Button key={f.path} as="a" href={f.url} target="_blank" rel="noopener" size="sm" colorScheme="cyan" leftIcon={<Download size={14} />}>
                  {r.files.length > 1 ? `Part ${i + 1}` : 'Download'}
                </Button>
              ))}
              {['starting', 'recording'].includes(r.status) && (
                <Button size="sm" colorScheme="red" isLoading={busy === r.id} onClick={() => stop(r.id)}>Stop</Button>
              )}
              {!['starting', 'recording', 'stopping'].includes(r.status) && (
                <Button size="sm" variant="ghost" color="gray.400" leftIcon={<Trash2 size={14} />} isLoading={busy === r.id} onClick={() => remove(r.id)}>Delete</Button>
              )}
            </HStack>
          </Box>
        );
      })}
    </VStack>
  );
};

const MyRecordingsPage: React.FC = () => {
  const navigate = useNavigate();
  return (
    <Container maxW="3xl" py={6}>
      <HStack mb={4} spacing={3}>
        <Button size="sm" variant="ghost" color="gray.300" leftIcon={<ArrowLeft size={16} />} onClick={() => navigate(-1)}>Back</Button>
        <Heading size="md" color={POKER_COLORS.cyan}>My recordings</Heading>
      </HStack>
      <RecordingsPanel />
    </Container>
  );
};

export default MyRecordingsPage;
